import { useState, useEffect, useCallback, RefObject } from 'react';
import { Capacitor } from '@capacitor/core';
import { HapticFeedback } from './HapticFeedback';

interface PullToRefreshOptions {
    threshold?: number; // Distance to pull before triggering (default: 80px)
    maxPull?: number; // Maximum pull distance (default: 120px)
    onRefresh: () => Promise<void>;
    enabled?: boolean;
}

/**
 * Pull-to-Refresh Hook for Native-like Experience
 * 
 * Usage:
 * const containerRef = useRef<HTMLDivElement>(null);
 * const { isPulling, pullDistance } = usePullToRefresh({
 *     ref: containerRef,
 *     onRefresh: async () => {
 *         await fetchData();
 *     }
 * });
 */
export const usePullToRefresh = (
    ref: RefObject<HTMLDivElement | null>,
    options: PullToRefreshOptions
) => {
    const {
        threshold = 80,
        maxPull = 120,
        onRefresh,
        enabled = true
    } = options;

    const [isPulling, setIsPulling] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [pullDistance, setPullDistance] = useState(0);
    const [startY, setStartY] = useState(0);
    const [startX, setStartX] = useState(0);
    const [canPull, setCanPull] = useState(false);
    const touchSlop = 16;
    const pullActivationSlop = 24;

    const isNative = Capacitor.isNativePlatform();

    const isInteractiveTarget = (target: EventTarget | null) => {
        if (!(target instanceof Element)) return false;

        return Boolean(
            target.closest(
                'button, a, input, textarea, select, label, [role="button"], [data-no-pull-refresh="true"]'
            )
        );
    };

    const handleTouchStart = useCallback((e: Event) => {
        const touchEvent = e as TouchEvent;
        if (!enabled || !ref.current) return;

        // Never start pull-to-refresh from interactive controls.
        if (isInteractiveTarget(touchEvent.target)) {
            setCanPull(false);
            return;
        }

        const scrollTop = ref.current.scrollTop || window.scrollY;
        
        // Only allow pull-to-refresh when at the top
        if (scrollTop === 0) {
            setStartX(touchEvent.touches[0].clientX);
            setStartY(touchEvent.touches[0].clientY);
            setCanPull(true);
        }
    }, [enabled, ref]);

    const handleTouchMove = useCallback((e: Event) => {
        const touchEvent = e as TouchEvent;
        if (!canPull || !enabled || isRefreshing) return;

        const currentX = touchEvent.touches[0].clientX;
        const currentY = touchEvent.touches[0].clientY;
        const distanceX = Math.abs(currentX - startX);
        const distanceY = currentY - startY;

        // Let regular taps and tiny movements pass through so clicks still fire.
        if (distanceY <= touchSlop) {
            return;
        }

        // Ignore horizontal gestures; pull-to-refresh should only react to vertical drags.
        if (distanceX > distanceY) {
            return;
        }

        // Only pull down
        if (distanceY > 0) {
            // Wait for a clearer vertical drag before hijacking browser/default behavior.
            if (distanceY <= pullActivationSlop) {
                return;
            }

            // Prevent default scroll
            e.preventDefault();
            
            // Calculate pull distance with resistance
            const resistance = 0.5;
            const calculatedDistance = Math.min((distanceY - pullActivationSlop) * resistance, maxPull);
            
            setPullDistance(calculatedDistance);
            setIsPulling(true);

            // Haptic feedback at threshold
            if (calculatedDistance >= threshold && pullDistance < threshold && isNative) {
                HapticFeedback.light();
            }
        }
    }, [canPull, enabled, isRefreshing, startX, startY, maxPull, threshold, pullDistance, isNative]);

    const handleTouchEnd = useCallback(async () => {
        if (!canPull || !enabled) return;

        setCanPull(false);

        // Trigger refresh if pulled beyond threshold
        if (pullDistance >= threshold && !isRefreshing) {
            setIsRefreshing(true);
            
            // Haptic feedback on trigger
            if (isNative) {
                HapticFeedback.medium();
            }

            try {
                await onRefresh();
                
                // Success haptic
                if (isNative) {
                    HapticFeedback.success();
                }
            } catch (error) {
                console.error('Pull-to-refresh error:', error);
                
                // Error haptic
                if (isNative) {
                    HapticFeedback.error();
                }
            } finally {
                setIsRefreshing(false);
            }
        }

        // Reset
        setIsPulling(false);
        setPullDistance(0);
    }, [canPull, enabled, pullDistance, threshold, isRefreshing, onRefresh, isNative]);

    useEffect(() => {
        const element = ref.current;
        if (!element || !enabled) return;

        element.addEventListener('touchstart', handleTouchStart, { passive: true });
        element.addEventListener('touchmove', handleTouchMove, { passive: false });
        element.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            element.removeEventListener('touchstart', handleTouchStart);
            element.removeEventListener('touchmove', handleTouchMove);
            element.removeEventListener('touchend', handleTouchEnd);
        };
    }, [enabled, ref, handleTouchStart, handleTouchMove, handleTouchEnd]);

    return {
        isPulling,
        isRefreshing,
        pullDistance,
        progress: Math.min(pullDistance / threshold, 1),
    };
};
